export const initialGraph = () => ({
  version: 2,
  start: null,
  __trigger: { x: 40, y: 160 },
  nodes: {},
});

export const defaults = (kind, timezone) => {
  if (kind === 'condition')
    return { definition: { context: 'appointment', match: 'all', rules: [] } };
  if (kind === 'wait')
    return {
      mode: 'dynamic',
      source: 'appointment',
      field: '',
      date_format: '',
      amount: null,
      unit: 'hours',
      direction: 'before',
      timezone,
      if_past: 'skip',
    };
  if (kind === 'action') return { template_id: null, routes: [], ttl_hours: 0 };
  if (kind === 'availability')
    return { channel: '', inbox_id: null, source_mode: 'appointment' };
  if (kind === 'message')
    return {
      channel: '',
      inbox_id: null,
      source_mode: 'appointment',
      template_id: null,
      success_on: 'accepted',
      timeout_minutes: null,
      ttl_hours: 0,
    };
  return {};
};

export const outputKeys = kind => {
  if (kind === 'condition') return ['next_true', 'next_false'];
  if (kind === 'availability')
    return ['next_available', 'next_unavailable', 'next_unknown'];
  if (kind === 'message')
    return [
      'next_success',
      'next_unavailable',
      'next_unknown',
      'next_failed',
      'next_timeout',
    ];
  return kind === 'exit' ? [] : ['next'];
};

const edge = (source, target, sourceHandle = 'next') => ({
  id: `${source}:${sourceHandle}`,
  source,
  target,
  sourceHandle,
  type: 'smoothstep',
});

export const fromGraph = (graph, trigger) => {
  const { __trigger: triggerPosition } = graph;
  const nodes = [
    {
      id: 'trigger',
      type: 'mres3',
      position: triggerPosition || { x: 40, y: 160 },
      data: { ...JSON.parse(JSON.stringify(trigger)), kind: 'trigger' },
    },
  ];
  const edges = graph.start ? [edge('trigger', graph.start)] : [];
  Object.entries(graph.nodes || {}).forEach(([id, node], index) => {
    nodes.push({
      id,
      type: 'mres3',
      hidden: node.type === 'exit' && node.editor_terminal === true,
      position: { x: node.x ?? 360 + index * 280, y: node.y ?? 160 },
      data: { ...JSON.parse(JSON.stringify(node)), kind: node.type },
    });
    outputKeys(node.type).forEach(output => {
      if (node[output]) edges.push(edge(id, node[output], output));
    });
  });
  return { nodes, edges };
};

export const toGraph = (nodes, edges) => {
  const start = nodes.find(node => node.id === 'trigger');
  const { kind, ...trigger } = start.data;
  const graph = { ...initialGraph(), __trigger: { ...start.position } };
  nodes
    .filter(node => node.id !== 'trigger')
    .forEach(node => {
      const { kind: type, ...data } = node.data;
      // Connections are the single source of truth, including deleted outcome edges.
      [
        'next',
        'next_true',
        'next_false',
        'next_available',
        'next_unavailable',
        'next_unknown',
        'next_success',
        'next_failed',
        'next_timeout',
      ].forEach(key => delete data[key]);
      graph.nodes[node.id] = {
        ...data,
        type,
        x: node.position.x,
        y: node.position.y,
      };
    });
  edges.forEach(connection => {
    if (connection.source === 'trigger') graph.start = connection.target;
    else if (
      graph.nodes[connection.source] &&
      outputKeys(graph.nodes[connection.source].type).includes(
        connection.sourceHandle || 'next'
      )
    )
      graph.nodes[connection.source][connection.sourceHandle || 'next'] =
        connection.target;
  });
  return { trigger, graph };
};

export const previousFields = graph =>
  [
    ...new Set(
      Object.values(graph.nodes || {}).flatMap(node => {
        const rules = (node.definition?.rules || [])
          .filter(rule => rule.source === 'previous' || rule.op === 'changed')
          .map(rule => rule.field);
        if (node.type === 'wait' && node.source === 'previous')
          rules.push(node.field);
        return rules;
      })
    ),
  ].filter(Boolean);
